import type { LucideProps } from 'lucide-react';
import {
  BadgeCheck,
  Bookmark,
  BookText,
  ChartBarIncreasing,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleMinus,
  CirclePlay,
  CirclePlus,
  CircleUser,
  CircleX,
  Clock,
  CreditCard,
  Dot,
  Download,
  Earth,
  ExternalLink,
  Eye,
  Gauge,
  Globe,
  Info,
  ListFilter,
  Lock,
  LockKeyhole,
  Mail,
  Mails,
  Menu,
  Minus,
  Package,
  Pencil,
  Plus,
  Podcast,
  RefreshCw,
  Search,
  Share2,
  SquareArrowOutUpRight,
  Star,
  StarHalf,
  Trash2,
  TriangleAlert,
  Upload,
  Video,
  X,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import type { ComponentType, RefAttributes, SVGProps } from 'react';

type SvgAttributes = Partial<SVGProps<SVGSVGElement>>;
type ElementAttributes = RefAttributes<SVGSVGElement> & SvgAttributes;

const FortuneIconName = [
  'AmexLogo',
  'ApplePayLogo',
  'ButtonDown',
  'ButtonLeft',
  'ButtonMinus',
  'ButtonPencil',
  'ButtonPlus',
  'ButtonRight',
  'ButtonSearch',
  'ButtonShare',
  'ButtonUp',
  'CirclePlayFilled',
  'DiscoverLogo',
  'Facebook',
  'FormInfo',
  'FormHidePassword',
  'FormShowPassword',
  'Fortune500Logo',
  'GooglePayLogo',
  'GooglePayMark',
  'Instagram',
  'LinkedIn',
  'MasterCardLogo',
  'PayPalLogo',
  'PayPalLogoSmall',
  'Pinterest',
  'SpinningLoader',
  'Twitter',
  'UnionPayLogo',
  'VisaLogo',
] as const;

const LucideIconName = [
  'BadgeCheck',
  'Bookmark',
  'BookText',
  'ChartBarIncreasing',
  'Check',
  'ChevronDown',
  'ChevronLeft',
  'ChevronRight',
  'ChevronUp',
  'CircleCheck',
  'CircleMinus',
  'CirclePlay',
  'CirclePlus',
  'CircleUser',
  'CircleX',
  'Clock',
  'CreditCard',
  'Dot',
  'Download',
  'Earth',
  'Eye',
  'Gauge',
  'Globe',
  'ExternalLink',
  'Info',
  'ListFilter',
  'Lock',
  'LockKeyhole',
  'Mail',
  'Mails',
  'Menu',
  'Minus',
  'Package',
  'Pencil',
  'Plus',
  'Podcast',
  'RefreshCw',
  'Search',
  'Share2',
  'SquareArrowOutUpRight',
  'Star',
  'StarHalf',
  'Trash2',
  'TriangleAlert',
  'Upload',
  'Video',
  'X',
] as const;

export type DynamicLucideIcon = ComponentType<LucideProps>;

export interface FortuneIconProps extends ElementAttributes {
  size?: string | number;
}

export type AllIconProps = LucideProps | FortuneIconProps;

export type FortuneIconName = (typeof FortuneIconName)[number];
export type LucideIconName = (typeof LucideIconName)[number];
export type IconName = LucideIconName | FortuneIconName;

export type DynamicFortuneIcon = ComponentType<FortuneIconProps>;
export type DynamicIcon = ComponentType<AllIconProps>;

export type FortuneIconMap = {
  [Key in FortuneIconName]: DynamicFortuneIcon;
};

export type LucideIconMap = {
  [Key in LucideIconName]: DynamicLucideIcon;
};

export type IconMap = {
  [Key in IconName]: DynamicIcon;
};

// biome-ignore-start lint/style/useNamingConvention: <using lucide component names>
export const lucideIconMap: LucideIconMap = {
  BadgeCheck,
  Bookmark,
  BookText,
  ChartBarIncreasing,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleMinus,
  CirclePlay,
  CirclePlus,
  CircleUser,
  CircleX,
  Clock,
  CreditCard,
  Dot,
  Download,
  Earth,
  ExternalLink,
  Eye,
  Gauge,
  Globe,
  Info,
  ListFilter,
  Lock,
  LockKeyhole,
  Mail,
  Mails,
  Menu,
  Minus,
  Package,
  Pencil,
  Plus,
  Podcast,
  RefreshCw,
  Search,
  Share2,
  SquareArrowOutUpRight,
  Star,
  StarHalf,
  Trash2,
  TriangleAlert,
  Upload,
  Video,
  X,
};
// biome-ignore-end lint/style/useNamingConvention: <>

// biome-ignore-start lint/style/useNamingConvention: <using component names>
export const fortuneIconMap: FortuneIconMap = {
  AmexLogo: dynamic(() => import('@/app_components/icons/logos/AmexLogo').then((mod) => mod.AmexLogo)),
  ApplePayLogo: dynamic(() => import('@/app_components/icons/logos/ApplePayLogo').then((mod) => mod.ApplePayLogo)),
  ButtonDown: dynamic(() => import('@/app_components/icons/buttons/ButtonDown').then((mod) => mod.ButtonDown)),
  ButtonLeft: dynamic(() => import('@/app_components/icons/buttons/ButtonLeft').then((mod) => mod.ButtonLeft)),
  ButtonMinus: dynamic(() => import('@/app_components/icons/buttons/ButtonMinus').then((mod) => mod.ButtonMinus)),
  ButtonPencil: dynamic(() => import('@/app_components/icons/buttons/ButtonPencil').then((mod) => mod.ButtonPencil)),
  ButtonPlus: dynamic(() => import('@/app_components/icons/buttons/ButtonPlus').then((mod) => mod.ButtonPlus)),
  ButtonRight: dynamic(() => import('@/app_components/icons/buttons/ButtonRight').then((mod) => mod.ButtonRight)),
  ButtonSearch: dynamic(() => import('@/app_components/icons/buttons/ButtonSearch').then((mod) => mod.ButtonSearch)),
  ButtonShare: dynamic(() => import('@/app_components/icons/buttons/ButtonShare').then((mod) => mod.ButtonShare)),
  ButtonUp: dynamic(() => import('@/app_components/icons/buttons/ButtonUp').then((mod) => mod.ButtonUp)),
  CirclePlayFilled: dynamic(() =>
    import('@/app_components/icons/CirclePlayFilled').then((mod) => mod.CirclePlayFilled),
  ),
  DiscoverLogo: dynamic(() => import('@/app_components/icons/logos/DiscoverLogo').then((mod) => mod.DiscoverLogo)),
  Facebook: dynamic(() => import('@/app_components/icons/socials/Facebook').then((mod) => mod.Facebook)),
  FormHidePassword: dynamic(() =>
    import('@/app_components/icons/forms/FormHidePassword').then((mod) => mod.FormHidePassword),
  ),
  FormInfo: dynamic(() => import('@/app_components/icons/forms/FormInfo').then((mod) => mod.FormInfo)),
  FormShowPassword: dynamic(() =>
    import('@/app_components/icons/forms/FormShowPassword').then((mod) => mod.FormShowPassword),
  ),
  Fortune500Logo: dynamic(() =>
    import('@/app_components/icons/logos/Fortune500Logo').then((mod) => mod.Fortune500Logo),
  ),
  GooglePayLogo: dynamic(() => import('@/app_components/icons/logos/GooglePayLogo').then((mod) => mod.GooglePayLogo)),
  GooglePayMark: dynamic(() => import('@/app_components/icons/logos/GooglePayMark').then((mod) => mod.GooglePayMark)),
  Instagram: dynamic(() => import('@/app_components/icons/socials/Instagram').then((mod) => mod.Instagram)),
  LinkedIn: dynamic(() => import('@/app_components/icons/socials/LinkedIn').then((mod) => mod.LinkedIn)),
  MasterCardLogo: dynamic(() =>
    import('@/app_components/icons/logos/MasterCardLogo').then((mod) => mod.MasterCardLogo),
  ),
  PayPalLogo: dynamic(() => import('@/app_components/icons/logos/PayPalLogo').then((mod) => mod.PayPalLogo)),
  PayPalLogoSmall: dynamic(() =>
    import('@/app_components/icons/logos/PayPalLogoSmall').then((mod) => mod.PayPalLogoSmall),
  ),
  Pinterest: dynamic(() => import('@/app_components/icons/socials/Pinterest').then((mod) => mod.Pinterest)),
  SpinningLoader: dynamic(() => import('@/app_components/icons/SpinningLoader').then((mod) => mod.SpinningLoader)),
  Twitter: dynamic(() => import('@/app_components/icons/socials/Twitter').then((mod) => mod.Twitter)),
  UnionPayLogo: dynamic(() => import('@/app_components/icons/logos/UnionPayLogo').then((mod) => mod.UnionPayLogo)),
  VisaLogo: dynamic(() => import('@/app_components/icons/logos/VisaLogo').then((mod) => mod.VisaLogo)),
};
// biome-ignore-end lint/style/useNamingConvention: <>
